import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type SubscriptionTier = 'stylist' | 'pro_stylist' | 'pro_competitor' | 'vip' | null;

interface SubscriptionState {
  subscribed: boolean;
  tier: SubscriptionTier;
  subscriptionEnd: string | null;
  loading: boolean;
  error: string | null;
}

const CACHE_KEY = 'hpe_sub_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCached(): (SubscriptionState & { ts: number }) | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > CACHE_TTL) return null;
    return parsed;
  } catch {
    return null;
  }
}

function setCache(state: Omit<SubscriptionState, 'loading' | 'error'>) {
  localStorage.setItem(CACHE_KEY, JSON.stringify({ ...state, ts: Date.now() }));
}

export function useSubscription() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  const [state, setState] = useState<SubscriptionState>(() => {
    const cached = getCached();
    if (cached) {
      return {
        subscribed: cached.subscribed,
        tier: cached.tier,
        subscriptionEnd: cached.subscriptionEnd,
        loading: false,
        error: null,
      };
    }
    return { subscribed: false, tier: null, subscriptionEnd: null, loading: true, error: null };
  });

  const checkSubscription = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setState({ subscribed: false, tier: null, subscriptionEnd: null, loading: false, error: null });
      localStorage.removeItem(CACHE_KEY);
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');

      if (error) throw new Error(error.message);

      const result = {
        subscribed: data.subscribed ?? false,
        tier: (data.tier as SubscriptionTier) ?? null,
        subscriptionEnd: data.subscription_end ?? null,
      };

      setCache(result);
      setState({ ...result, loading: false, error: null });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Subscription check failed';
      console.error('[useSubscription]', msg);
      // On error, don't block access if we have a cached state
      const cached = getCached();
      if (cached) {
        setState({
          subscribed: cached.subscribed,
          tier: cached.tier,
          subscriptionEnd: cached.subscriptionEnd,
          loading: false,
          error: msg,
        });
      } else {
        setState(prev => ({ ...prev, loading: false, error: msg }));
      }
    }
  }, [isAuthenticated, user]);

  // Check on auth change
  useEffect(() => {
    if (!authLoading) {
      checkSubscription();
    }
  }, [authLoading, checkSubscription]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(checkSubscription, 60_000);
    return () => clearInterval(interval);
  }, [isAuthenticated, checkSubscription]);

  return {
    ...state,
    refreshSubscription: checkSubscription,
    /** True if the user has any paid tier */
    hasPaidAccess: state.subscribed,
    /** Check if user has at least a given tier level */
    hasMinTier: (minTier: SubscriptionTier): boolean => {
      if (!state.subscribed || !state.tier || !minTier) return false;
      const rank: Record<string, number> = { stylist: 1, pro_stylist: 2, pro_competitor: 3, vip: 4 };
      return (rank[state.tier] ?? 0) >= (rank[minTier] ?? 0);
    },
  };
}
